<template>
    <b-card title="Visit History">

        <div class="d-flex justify-content-between">
            <div class="mb-3">
                <b-form inline @submit.prevent="loadShifts()">
                    <date-picker v-model="startDate" placeholder="Start Date">
                    </date-picker> &nbsp;to&nbsp;
                    <date-picker v-model="endDate" placeholder="End Date">
                    </date-picker>
                    &nbsp;&nbsp;<b-button type="submit" variant="info">Load Visits</b-button>
                </b-form>
            </div>

            <div class="mb-3">
                <b-col>
                    <b-button variant="success" :href="`/caregiver/shift/create?scopedUser=${scopeduser}`"
                        v-if="scopeduser">Add Manual Visit</b-button>
                </b-col>
            </div>
        </div>

        <loading-card v-show="loading"></loading-card>

        <div class="table-responsive">
            <b-table hover sort-by="null" :sort-desc="true" :items="items" :fields="fields"
                v-show="!loading">

                <template v-slot:cell(checked_in_time)="row">
                    <utc-time :timezone="row.item.shift_timezone" :time="row.item.checked_in_time"
                        format="MM/DD/YYYY" />
                </template>
                <template v-slot:cell(clocked_in)="row">
                    <utc-time :timezone="row.item.shift_timezone" :time="row.item.checked_in_time" format="hh:mm A" />
                </template>
                <template v-slot:cell(clocked_out)="row">
                    <span v-if="row.item.checked_out_time">
                        <utc-time :timezone="row.item.shift_timezone" :time="row.item.checked_out_time"
                            format="hh:mm A" />
                    </span>
                    <span v-else>
                        N/A
                    </span>
                </template>
                <template v-slot:cell(invoice_id)="row">
                    {{ row.item.invoice_id ? row.item.invoice_id : 'Pending' }}
                </template>
                <template v-slot:cell(ltc_submission)="row">
                    {{ row.item.invoice ? formatDate(row.item.invoice.created_at) : 'Pending' }}
                </template>
                <template v-slot:cell(verified)="row">
                    {{ row.item.verified ? 'Verified' : 'Unverified' }}
                </template>
                <template v-slot:cell(total)="row">
                    {{ moneyFormat(row.item.total) }}
                </template>
                <template v-slot:cell(visit_status)="row">
                    {{ determineVisitStatus(row.item) }}
                </template>
                <template v-slot:cell(actions)="row">
                    <b-btn @click="details(row.item)">View</b-btn>&nbsp;
                    <b-btn target="_blank" :href="`/caregiver/shifts/edit/${row.item.id}`" variant="info"
                        v-b-tooltip.hover title="Edit">Modify</b-btn>&nbsp;
                    <b-btn @click="dispute(row.item)" variant="danger">Dispute</b-btn>
                </template>
            </b-table>
        </div>

        <b-modal id="detailsModal" title="Visit Details" v-model="disputeModal" size="lg">
            <b-container fluid>
                <b-row>
                    <b-col sm="12" class="mb-3">
                        <b-row>
                            <b-col sm="4" class="d-flex align-items-center">
                                <label for="dispute-reason">Dispute Reason</label>
                            </b-col>
                            <b-col sm="8">
                                <b-form-select 
                                    v-model="disputeForm.disputeReason" 
                                    id="dispute-reason" 
                                    required>
                                    <option value="">Please select a reason</option>
                                    
                                    <option v-for="reason in caregiverdisputereason" :key="reason.id" :value="reason.id">
                                        {{ reason.reason }}
                                    </option>
                                </b-form-select>
                                <b-form-invalid-feedback>
                                    Dispute Reason is required.
                                </b-form-invalid-feedback>
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col sm="12" class="mb-3">
                        <b-row>
                            <b-col sm="4" class="d-flex align-items-center">
                                <label for="dispute-reason">Instructions to Claimant</label>
                            </b-col>
                            <b-col sm="8">
                                <b-textarea :rows="5" v-model="disputeForm.notes"></b-textarea>
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col sm="12" v-if="disputeForm.disputeReason === 1" class="mt-3">
                        <p>
                            <a href="/business/knowledge-manager/26" target="_blank">A link to knowledge center entry showing how to modify a visit.</a>
                        </p>
                    </b-col>
                </b-row>
            </b-container>
            <div slot="modal-footer">
                <b-btn variant="info" :disabled="hasSubmitted" @click="disputeSelected()"><i class="fa fa-save"></i>
                    Save
                    Dispute</b-btn>
                <b-btn variant="default" @click="disputeModal = false">Close</b-btn>
            </div>
        </b-modal>

        <b-modal id="disputeDetailsModal" title="Visit Details" v-model="detailsModal" size="lg">
            <b-container fluid>
                <b-row class="with-padding-bottom">
                    <b-col sm="6" v-if="selectedItem.client">
                        <strong>Claimant</strong>
                        <br />
                        {{ selectedItem.client.name }}
                    </b-col>
                    <b-col sm="6" v-if="caregiver.name">
                        <strong>Provider</strong><br />
                        {{ caregiver.name }}
                    </b-col>
                </b-row>
                <b-row class="with-padding-bottom">
                    <b-col sm="6">
                        <strong>Checked In Time</strong><br />
                        {{ formatDateTimeFromUTC(selectedItem.checked_in_time) }}
                    </b-col>
                    <b-col sm="6">
                        <strong>Checked Out Time</strong><br />
                        {{ selectedItem.checked_out_time ? formatDateTimeFromUTC(selectedItem.checked_out_time) : 'N/A'
                        }}<br />
                    </b-col>
                </b-row>

                <b-row class="with-padding-bottom" v-if="selectedItem.signature">
                    <b-col>
                        <strong>Claimant Signature</strong>
                        <div v-html="selectedItem.signature.content" class="signature"></div>
                    </b-col>
                </b-row>


                <b-row class="with-padding-bottom">
                    <b-col sm="6">
                        <strong>Did the client fall?</strong><br />
                        <span v-if="selectedItem.client_fall">Yes</span>
                        <span v-else>No</span>
                    </b-col>
                    <b-col sm="6">
                        <strong>Client's Condition</strong><br />
                        <span v-if="selectedItem.client_condition > 0">Improved</span>
                        <span v-else-if="selectedItem.client_condition < 0">Declined</span>
                        <span v-else>No Change</span>
                    </b-col>
                </b-row>

                <b-row class="with-padding-bottom" v-if="hospital_stays_enabled">
                    <b-col sm="6 py-2">
                        <strong>Has the client had any hospital stays since your last visit?</strong><br />
                        <span v-if="selectedItem.hospital_stay">Yes</span>
                        <span v-else>No</span>
                    </b-col>

                    <b-col sm="6 py-2" v-if="hospital_stays_enhanced_flow && selectedItem.hospital_stay">
                        <strong>Is the client still currently in the hospital?</strong><br />
                        <span v-if="selectedItem.still_in_hospital">Yes</span>
                        <span v-else>No</span>
                    </b-col>

                    <b-col sm="6 py-2" v-if="selectedItem.modification_reasons.length">
                        <strong>What are the reasons for modification?</strong><br />
                        <span>{{ selectedItem.modification_reasons.join(", ") }}</span>
                    </b-col>

                    <b-col sm="6 py-2" v-if="selectedItem.hasOwnProperty('manual_addition_reason') && selectedItem.manual_addition_reason && selectedItem.manual_addition_reason.trim() !== ''">
                        <strong>What is the reason for the manual addition?</strong><br />
                        <span>{{ selectedItem.manual_addition_reason }}</span>
                    </b-col> 

                    <b-col sm="6 py-2" v-if="hospital_stays_enhanced_flow && selectedItem.hospital_stay">
                        <strong>Hospital stay dates</strong><br />
                        <span>
                            {{ selectedItem.hospital_stay_from_date }}
                            &nbsp; to &nbsp;
                            <span v-if="selectedItem.still_in_hospital">Currently in the hospital</span>
                            <span v-else>{{ selectedItem.hospital_stay_to_date }}</span>
                        </span>
                    </b-col>
                </b-row>

                <strong>Activities Performed</strong>
                <b-row>
                    <b-col sm="12">
                        <p v-if="!selectedItem.activities || !selectedItem.activities.length">
                            No activities recorded
                        </p>
                        <table class="table table-sm" v-else>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="activity in selectedItem.activities" :key="activity.id">
                                    <td>{{ activity.name }}</td>
                                    <td>{{ activity.value }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </b-col>
                </b-row>
            </b-container>
            <div slot="modal-footer">
                <!--<b-btn variant="default" @click="downloadSelected()"><i class="fa fa-file-pdf-o"></i> Download PDF</b-btn>-->
                <!--<b-btn variant="primary" @click="printSelected()"><i class="fa fa-print"></i> Print</b-btn>-->
                <b-btn variant="default" @click="detailsModal = false">Close</b-btn>
            </div>
        </b-modal>
    </b-card>
</template>

<script>
import axios from 'axios';
import FormatsDates from '../../mixins/FormatsDates';
import FormatsNumbers from '../../mixins/FormatsNumbers';

export default {
    props: ['caregiver', 'scopeduser', 'caregiverdisputereason'],

    mixins: [FormatsDates, FormatsNumbers],

    data() {
        return {
            hospital_stays_enabled: false,
            hospital_stays_enhanced_flow: false,
            items: [],
            fields: [
                {
                    key: 'checked_in_time',
                    label: 'Date',
                    // formatter: (value) => { return this.formatDate(value) }
                },
                'clocked_in',
                'clocked_out',
                {
                    key: 'duration',
                    label: 'Hours',
                    formatter: value => this.formatHours(value),
                },
                // {
                //     key: 'activities',
                //     formatter: (value) => {
                //         let count = 0;
                //         return value.map(function(item) {
                //             if (count++ === 0) { return item.name }
                //             return ',' + item.name;
                //         })
                //     }
                // },
                {
                    key: 'client',
                    formatter: (value) => { return value.name }
                },
                {
                    key: 'total',
                    label: 'Visit Total',
                    formatter: (value) => { return this.moneyFormat(value) }
                },
                {
                    key: 'visit_status',
                    label: 'Visit Status',
                },
                {
                    key: 'actions',
                    class: 'hidden-print'
                }
            ],
            disputeModal: false,
            hasSubmitted: false,
            disputeForm: new Form({ 'notes': '', 'disputeReason': '' }),
            detailsModal: false,
            selectedItem: {},
            startDate: moment().subtract(14, 'days').format('MM/DD/YYYY'),
            endDate: moment().format('MM/DD/YYYY'),
            loading: false,
        }
    },

    methods: {
        formatHours(hours) {
            if (!hours) {
                return '-';
            }
            let minutes = Math.floor((hours * 60) % 60);
            hours = Math.floor(hours);
            return `${hours}h ${minutes}m`;
        },
        async loadShifts() {
            this.loading = true;
            let startDate = moment(this.startDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
            let endDate = moment(this.endDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
            const response = await axios.get('/caregiver/visit-history/shifts?start_date=' + startDate + '&end_date=' + endDate);
            this.items = response.data;
            this.loading = false;
        },


        start_end(data) {
            if (data.item.week) {
                if ('start' in data.item.week) {
                    return `${this.formatDate(data.item.week.start)} - ${this.formatDate(data.item.week.end)}`;
                }
            }
            return 'Shift N/A';
        },

        async disputeSelected() {
            // TODO Disable Button
            this.hasSubmitted = true;

            let url = '/caregiver/visit-history/' + this.selectedItem.id + '/dispute';
            await this.disputeForm.post(url);
            this.selectedItem.dispute = { 'notes': this.disputeForm.notes, 'disputeReason': this.disputeForm.disputeReason };
            this.disputeModal = false;
        },

        details(item) {

            this.hospital_stays_enabled = item.client.carrier.hospital_stays_enabled;
            this.hospital_stays_enhanced_flow = item.client.carrier.hospital_stays_enhanced_flow;

            this.selectedItem = Object.assign({}, item);
            this.selectedItem.hospital_stay_from_date = this.convertDateYmdToMdy(this.selectedItem.hospital_stay_from_date);
            this.selectedItem.hospital_stay_to_date = this.convertDateYmdToMdy(this.selectedItem.hospital_stay_to_date);
            this.disputeModal = false;
            this.detailsModal = true;
        },

        modify(item) {
            // URL should be caregiver/shift/{ID}
            this.selectedItem = Object.assign({}, item);
            let url = '/caregiver/shifts/edit/' + this.selectedItem.id + "?scopedUser=" + this.scopeduser
            window.open(url);
        },

        dispute(item) {
            this.hasSubmitted = false;
            this.selectedItem = item;
            this.disputeForm.notes = this.selectedItem.dispute ? this.selectedItem.dispute.notes : '';
            this.disputeForm.disputeReason = this.selectedItem.dispute ? this.selectedItem.dispute.disputeReason : ''; 
            this.detailsModal = false;
            this.disputeModal = true;
        },       

        determineVisitStatus(item) {
            if (item.shift_in_invoices > 0) {
                return "Complete"
            }

            if (item.under_24_hours && item.has_adls) {
                return "Complete"
            }

            if (!item.under_24_hours) {
                return "Requires Attention"
            }

            if (!item.has_adls) {
                return "Requires Attention"
            }

            return "Complete"
        },

        determineDisplayTotal(item) {
            let visit_total = 0.00;

            if (item.shift_in_invoices > 0) {
                visit_total = item.total;
            } else if (!item.has_adls) {
                visit_total = 0
            } else if (!item.under_24_hours) {
                visit_total = 0
            } else {
                visit_total = item.total
            }

            return this.moneyFormat(visit_total)
        }
    },

    mounted() {
        this.loadShifts();
    }
}
</script>
